import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { FaPlane, FaSatellite, FaCloud, FaNetworkWired, FaBrain, FaChartLine } from 'react-icons/fa';
import backgroundImage from '../../assets/images/c-room.jpg';
import dispatch from '../../assets/images/dispatch.png';
import pilot from '../../assets/images/pilot.png';


const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideIn = keyframes`
  from {
    transform: translateX(-20px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const fadeInOnScroll = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  position: relative;
  background: transparent;
  color: #fff;
  isolation: isolate;
  scroll-behavior: smooth;
  overflow-y: auto;

  /* Custom Scrollbar */
  ::-webkit-scrollbar {
    width: 10px;
  }

  ::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.3);
  }

  ::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.8);
    border-radius: 5px;
    
    &:hover {
      background: rgba(255, 255, 255, 1);
    }
  }

  /* Smooth Scroll Effect */
  & {
    scroll-behavior: smooth;
    -webkit-overflow-scrolling: touch;
  }
`;

const ContentContainer = styled.div`
  position: relative;
  z-index: 50;
  pointer-events: auto;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  animation: ${fadeIn} 0.8s ease-out;
  scroll-behavior: smooth;
`;

const HeroSection = styled.div`
  position: relative;
  width: 100%;
  height: 100vh;
  background: url(${backgroundImage}) no-repeat center center;
  background-size: cover;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.7) 100%);
  }
`;

const HeroContent = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 max(5%, 24px);
  display: flex;
  flex-direction: column;
`;

const BottomContent = styled.div`
  margin-top: auto;
  margin-bottom: 120px;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
`;

const TitleWrapper = styled.div`
  flex: 1;
`;

const TitleContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Tag = styled.div`
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: rgba(255, 255, 255, 0.6);
  font-weight: 400;
  opacity: 0;
  animation: ${fadeIn} 0.8s ease forwards;
  animation-delay: 0.2s;
`;

const Title = styled.h1`
  font-size: clamp(2.5rem, 6vw, 5.5rem);
  color: white;
  line-height: 1;
  font-weight: 500;
  letter-spacing: -0.02em;
  text-align: left;
  max-width: 15ch;
  opacity: 0;
  animation: ${fadeIn} 0.8s ease forwards;
  animation-delay: 0.4s;
`;

const DescriptionWrapper = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-end;
  padding-right: 0;
  margin-right: -5%;
`;

const Description = styled.p`
  font-size: clamp(1.125rem, 1.5vw, 1.5rem);
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.6;
  max-width: 400px;
  text-align: left;
  font-weight: 400;
  margin-right: 5%;
  opacity: 0;
  animation: ${fadeIn} 0.8s ease forwards;
  animation-delay: 0.6s;
`;

const MainContent = styled.div`
  position: relative;
  background: #000;
`;

const KeyFeatures = styled.div`
  padding: 120px 5%;
  background: #000;
`;

const KeyFeaturesContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  padding: 120px 0 60px;
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding-left: 24px;
`;

const SectionHeaderTitle = styled.h2`
  font-size: 3rem;
  color: white;
  line-height: 1.1;
  font-weight: 500;
  opacity: 0;
  visibility: ${props => props.$isVisible ? 'visible' : 'hidden'};
  animation: ${props => props.$isVisible ? fadeInOnScroll : 'none'} 1s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  margin-left: 0;
`;

const SectionTitle = styled.h2`
  font-size: 3rem;
  margin-bottom: 4rem;
  color: white;
  text-align: left;
  opacity: 0;
  visibility: ${props => props.$isVisible ? 'visible' : 'hidden'};
  animation: ${props => props.$isVisible ? fadeInOnScroll : 'none'} 1s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  margin-left: 24px;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SectionBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: url(${props => props.$image}) no-repeat center center;
  background-size: cover;
  opacity: ${props => props.$isActive ? 1 : 0};
  transition: opacity 0.5s ease;
`;

const DetailSection = styled.div`
  padding: 0 24px;
  background: #000;
  position: relative;
  display: flex;
  max-width: 1400px;
  margin: 0 auto;
  margin-bottom: 80px;
`;

const DetailContainer = styled.div`
  width: 100%;
  display: flex;
  position: relative;
  gap: 0;
  background: #000;
`;

const NavSquare = styled.div`
  width: 8px;
  height: 8px;
  background: ${props => props.$isActive ? '#FF4D00' : 'rgba(255, 77, 0, 0.3)'};
  margin-bottom: 1rem;
  opacity: 0;
  animation: ${fadeIn} 0.5s ease forwards;
  animation-delay: 0.2s;
`;

const NavTitle = styled.div`
  font-size: 1.25rem;
  color: white;
  font-weight: 500;
  margin-bottom: ${props => props.$isActive ? '1rem' : '0'};
  opacity: 0;
  animation: ${slideIn} 0.5s ease forwards;
  animation-delay: 0.3s;
`;

const NavDescription = styled.div`
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.8);
  max-width: 400px;
  line-height: 1.6;
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.5s ease;
  animation: ${fadeIn} 0.5s ease forwards;
  animation-delay: 0.4s;
`;

const SectionColumn = styled.div`
  position: relative;
  flex: 1;
  width: ${props => props.$isActive ? '600px' : '200px'};
  height: 600px;
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  cursor: pointer;
  background: #000;
  border-right: 1px solid rgba(255, 255, 255, 0.2);

  &:last-child {
    border-right: none;
  }

  &:hover {
    width: 600px;
  }
`;

const SectionContent = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 2rem;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  background: ${props => props.$isActive ? 'linear-gradient(0deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 100%)' : 'none'};
  height: ${props => props.$isActive ? '50%' : 'auto'};
  
  ${NavSquare}, ${NavTitle}, ${NavDescription} {
    animation-play-state: ${props => props.$isActive ? 'running' : 'paused'};
  }

  ${SectionColumn}:hover & {
    background: linear-gradient(0deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 100%);
    height: 50%;

    ${NavSquare}, ${NavTitle}, ${NavDescription} {
      animation-play-state: running;
    }
  }
`;

const FeatureCard = styled.div`
  padding: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  opacity: 0;
  visibility: ${props => props.$isVisible ? 'visible' : 'hidden'};
  animation: ${props => props.$isVisible ? fadeInOnScroll : 'none'} 1s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  animation-delay: ${props => props.$isVisible ? props.$index * 0.15 : 0}s;
`;

const FeatureNumber = styled.div`
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.4);
  margin-bottom: 2rem;
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: white;
`;

const FeatureDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.6);
`;

function AirfieldOperations() {
  const [activeSection, setActiveSection] = useState('dispatcher');
  const [headerVisible, setHeaderVisible] = useState(false);
  const [featuresVisible, setFeaturesVisible] = useState(false);
  
  const headerRef = useRef(null);
  const featuresRef = useRef(null);

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);

    const observerOptions = {
      threshold: 0.15,
      rootMargin: '50px'
    };

    const headerObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setHeaderVisible(true);
          headerObserver.unobserve(entry.target);
        }
      });
    }, observerOptions);

    const featuresObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setFeaturesVisible(true);
          featuresObserver.unobserve(entry.target);
        }
      });
    }, observerOptions);

    if (headerRef.current) {
      headerObserver.observe(headerRef.current);
    }
    if (featuresRef.current) {
      featuresObserver.observe(featuresRef.current);
    }

    return () => {
      if (headerRef.current) headerObserver.unobserve(headerRef.current);
      if (featuresRef.current) featuresObserver.unobserve(featuresRef.current);
    };
  }, []);

  const sections = [
    {
      id: 'dispatcher',
      title: 'Dispatcher',
      image: dispatch,
      description: 'Advanced dispatch optimization and fleet monitoring powered by GeoSpatial intelligence. Simulating the possible futures and identifying possible IROPs with Ultra-High-Resolution weather predictions and pattern analysis enable better planning and reduced operational costs.'
    },
    {
      id: 'pilot',
      title: 'Pilot',
      image: pilot,
      description: 'Enhanced situational awareness and real-time decision-making through seamless EFB integration. Real-time updates and route optimization powered by superior weather predictions and cloud-native GeoSpatial Intelligence.'
    },
    {
      id: 'atc',
      title: 'ATC',
      image: backgroundImage,
      description: 'Streamlined Vectoring and Coordination with comprehensive situational awareness and Trajectory Predictions. Automated tools and predictive analytics optimize traffic flow and reduce controller workload.'
    }
  ];

  const features = [
    {
      number: "01",
      title: "Superior Weather Prediction",
      description: "Ultra-high-resolution weather forecasting powered by GeoSpatial AI, combining multiple data sources to enable better flight planning and reduce weather-related delays."
    },
    {
      number: "02",
      title: "Intelligent Dispatch System",
      description: "Optimize flight planning with AI-powered tools that leverage advanced GeoSpatial data to reduce fuel consumption and improve operational efficiency."
    },
    {
      number: "03",
      title: "Enhanced EFB Integration",
      description: "Provide pilots with real-time GeoSpatial insights, weather updates, and route optimization through seamless Electronic Flight Bag integration."
    },
    {
      number: "04",
      title: "Collaborative Operations",
      description: "Connect dispatch, flight crews, and ATC through a unified platform with real-time situational awareness and automated coordination tools."
    },
    {
      number: "05",
      title: "Comprehensive Data Sources",
      description: "Leveraging more than 100 data sources including weather, traffic, airspace, and flight plans from Private and Public providers, we provide the most comprehensive and accurate predictions."
    },
    {
      number: "06",
      title: "Seamless Integration",
      description: "Building on the modular and flexible foundation, we offer APIs that can be easily integrate to your applications for real-time situational awareness and decision-making."
    }
  ];

  return (
    <PageContainer>
      <Navbar />
      <ContentContainer>
        <HeroSection>
          <HeroContent>
            <BottomContent>
              <TitleWrapper>
                <TitleContent>
                  <Tag>[GEOSPATIAL INTELLIGENCE + FLIGHT OPERATIONS]</Tag>
                  <Title>Airfield Operations</Title>
                </TitleContent>
              </TitleWrapper>
              <DescriptionWrapper>
                <Description>
                  Transform flight operations with superior weather prediction and GeoSpatial intelligence, enabling smarter decisions for dispatchers, pilots, and ATC.
                </Description>
              </DescriptionWrapper>
            </BottomContent>
          </HeroContent>
        </HeroSection>
        <MainContent>
          <SectionHeader ref={headerRef}>
            <SectionHeaderTitle $isVisible={headerVisible}>
              Enhanced operational Platform
            </SectionHeaderTitle>
          </SectionHeader>
          <DetailSection>
            <DetailContainer>
              {sections.map((section) => (
                <SectionColumn 
                  key={section.id}
                  $isActive={activeSection === section.id}
                  onMouseEnter={() => setActiveSection(section.id)}
                >
                  <SectionBackground 
                    $image={section.image}
                    $isActive={activeSection === section.id}
                  />
                  <SectionContent $isActive={activeSection === section.id}>
                    <NavSquare $isActive={activeSection === section.id} />
                    <NavTitle $isActive={activeSection === section.id}>
                      {section.title}
                    </NavTitle>
                    {activeSection === section.id && (
                      <NavDescription>
                        {section.description}
                      </NavDescription>
                    )}
                  </SectionContent>
                </SectionColumn>
              ))}
            </DetailContainer>
          </DetailSection>

          <KeyFeatures>
            <KeyFeaturesContent ref={featuresRef}>
              <SectionTitle $isVisible={featuresVisible}>
                Key Features + Platform
              </SectionTitle>
              <FeaturesGrid>
                {features.map((feature, index) => (
                  <FeatureCard 
                    key={index} 
                    $index={index}
                    $isVisible={featuresVisible}
                  >
                    <FeatureNumber>{feature.number}</FeatureNumber>
                    <FeatureTitle>{feature.title}</FeatureTitle>
                    <FeatureDescription>{feature.description}</FeatureDescription>
                  </FeatureCard>
                ))}
              </FeaturesGrid>
            </KeyFeaturesContent>
          </KeyFeatures>
          <Footer />
        </MainContent>
      </ContentContainer>
    </PageContainer>
  );
}

export default AirfieldOperations; 